import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { businessApi } from '../services/api';
import { Business } from '../types';
import { Modal } from '../components/Modal';

const CATEGORIES = [
  'Gimnasio','CrossFit','Yoga','Pilates','Natación','Boxeo',
  'Artes Marciales','Danza','Bienestar','Fisioterapia',
  'Padel','Fútbol','Atletismo','Ciclismo','Escalada','Otra',
];

interface BusinessForm {
  name: string;
  description: string;
  category: string;
  address: string;
  city: string;
  phone: string;
  email: string;
  website: string;
  instagram: string;
  whatsapp: string;
}

export function Businesses() {
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [planAlert, setPlanAlert] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState<Business | null>(null);

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<BusinessForm>();

  useEffect(() => { fetchBusinesses(); }, []);

  const fetchBusinesses = async () => {
    try {
      const r = await businessApi.getMyBusinesses();
      setBusinesses(r.data.data.businesses || []);
    } catch { toast.error('Error cargando negocios'); }
    finally { setIsLoading(false); }
  };

  const onSubmit = async (data: BusinessForm) => {
    try {
      if (editingBusiness) {
        await businessApi.updateBusiness(editingBusiness.id, data as any);
        toast.success('Negocio actualizado');
      } else {
        await businessApi.createBusiness(data as any);
        toast.success('Negocio creado ✅');
      }
      setIsModalOpen(false);
      setEditingBusiness(null);
      reset();
      fetchBusinesses();
    } catch (e: any) {
      const msg = e?.response?.data?.message || (editingBusiness ? 'Error al actualizar' : 'Error al crear');
      toast.error(msg);
    }
  };

  const handleEdit = (b: Business) => {
    setEditingBusiness(b);
    reset({
      name: b.name || '',
      description: b.description || '',
      category: (b as any).category || '',
      address: b.address || '',
      city: (b as any).city || '',
      phone: b.phone || '',
      email: b.email || '',
      website: b.website || '',
      instagram: (b as any).instagram || '',
      whatsapp: (b as any).whatsapp || '',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Eliminar este negocio? Esta acción no se puede deshacer.')) return;
    try {
      await businessApi.deleteBusiness(id);
      toast.success('Negocio eliminado');
      fetchBusinesses();
    } catch { toast.error('Error al eliminar'); }
  };

  const handleAddNew = () => {
    // Plan FREE: solo 1 negocio
    if (businesses.length >= 1) {
      setPlanAlert(true);
      return;
    }
    setEditingBusiness(null);
    reset({ name:'',description:'',category:'',address:'',city:'',phone:'',email:'',website:'',instagram:'',whatsapp:'' });
    setIsModalOpen(true);
  };

  if (isLoading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  );

  return (
    <div className="space-y-6">
      <style>{`
        .biz-card { background: white; border-radius: 20px; border: 1px solid #e2e8f0; overflow: hidden; transition: transform .2s, box-shadow .2s; }
        .biz-card:hover { transform: translateY(-3px); box-shadow: 0 12px 32px rgba(0,0,0,.08); }
        .admin-biz-cover { height: 100px; background: linear-gradient(135deg, #1e293b, #334155); position: relative; overflow: hidden; }
        .admin-biz-cover img { width: 100%; height: 100%; object-fit: cover; }
        .admin-biz-logo { position: absolute; bottom: -20px; left: 16px; width: 44px; height: 44px; border-radius: 10px; border: 3px solid white; background: linear-gradient(135deg, #3b82f6, #8b5cf6); display: flex; align-items: center; justify-content: center; font-weight: 700; color: white; font-size: 1.1rem; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,.15); }
        .admin-biz-logo img { width: 100%; height: 100%; object-fit: cover; }
        .biz-body { padding: 28px 16px 16px; }
        .biz-name { font-size: 1rem; font-weight: 700; color: #1e293b; margin-bottom: 4px; }
        .biz-meta { font-size: .78rem; color: #94a3b8; margin-bottom: 12px; }
        .biz-stats { display: flex; gap: 16px; padding: 12px 0; border-top: 1px solid #f1f5f9; border-bottom: 1px solid #f1f5f9; margin-bottom: 14px; }
        .biz-stat { text-align: center; flex: 1; }
        .biz-stat-num { font-size: 1.2rem; font-weight: 700; color: #1e293b; }
        .biz-stat-label { font-size: .68rem; color: #94a3b8; margin-top: 2px; }
        .biz-actions { display: flex; gap: 8px; }
        .cat-badge { display: inline-flex; background: #dbeafe; color: #1d4ed8; font-size: .68rem; font-weight: 700; padding: 2px 8px; border-radius: 100px; margin-bottom: 8px; }
      `}</style>

      {/* HEADER */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Mis negocios</h1>
          <p className="text-secondary-500 text-sm mt-1">Gestiona tus negocios y su presencia en FitPulse</p>
        </div>
        <button onClick={handleAddNew} className="btn-primary flex items-center gap-2">
          <PlusIcon className="h-5 w-5" />Nuevo negocio
        </button>
      </div>

      {businesses.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {businesses.map(b => (
            <div key={b.id} className="biz-card">
              <div className="admin-biz-cover">
                {(b as any).coverImage && <img src={(b as any).coverImage} alt={b.name} />}
                <div className="admin-biz-logo">
                  {(b as any).logo ? <img src={(b as any).logo} alt={b.name}/> : b.name.charAt(0).toUpperCase()}
                </div>
              </div>
              <div className="biz-body">
                {(b as any).category && <span className="cat-badge">{(b as any).category}</span>}
                <div className="biz-name">{b.name}</div>
                <div className="biz-meta">
                  {(b as any).city && `📍 ${(b as any).city}`}
                  {b.phone && ` · 📞 ${b.phone}`}
                </div>
                <div className="biz-stats">
                  <div className="biz-stat"><div className="biz-stat-num">{b._count?.services || 0}</div><div className="biz-stat-label">Servicios</div></div>
                  <div className="biz-stat"><div className="biz-stat-num">{b._count?.staff || 0}</div><div className="biz-stat-label">Personal</div></div>
                  <div className="biz-stat"><div className="biz-stat-num">{b._count?.bookings || 0}</div><div className="biz-stat-label">Reservas</div></div>
                </div>
                <div className="biz-actions">
                  <a href={`/businesses/${b.id}`} className="btn-secondary text-sm flex-1 flex items-center justify-center gap-1">
                    <EyeIcon className="h-4 w-4"/>Gestionar
                  </a>
                  <button onClick={() => handleEdit(b)} className="btn-secondary text-sm px-3">
                    <PencilIcon className="h-4 w-4"/>
                  </button>
                  <button onClick={() => handleDelete(b.id)} style={{padding:'8px 10px',borderRadius:8,background:'#fef2f2',color:'#ef4444',border:'none',cursor:'pointer'}}>
                    <TrashIcon className="h-4 w-4"/>
                  </button>
                </div>
                <a href={`https://fitpulse.es/b/${(b as any).slug}`} target="_blank" rel="noreferrer"
                  style={{display:'flex',alignItems:'center',gap:4,marginTop:10,fontSize:'.75rem',color:'#3b82f6',textDecoration:'none',fontWeight:600}}>
                  <GlobeAltIcon style={{width:14}}/>Ver página pública
                </a>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card p-12 text-center">
          <div style={{fontSize:'3.5rem',marginBottom:16}}>🏢</div>
          <p className="text-secondary-500 font-medium mb-2">No tienes negocios todavía</p>
          <p className="text-secondary-400 text-sm mb-6">Crea tu primer negocio para aparecer en el directorio de FitPulse</p>
          <button onClick={handleAddNew} className="btn-primary inline-flex items-center gap-2">
            <PlusIcon className="h-5 w-5"/>Crear mi primer negocio
          </button>
        </div>
      )}

      {/* MODAL */}
      {/* ALERTA PLAN */}
      {planAlert && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.5)',zIndex:200,display:'flex',alignItems:'center',justifyContent:'center',padding:20,backdropFilter:'blur(4px)'}}>
          <div style={{background:'white',borderRadius:20,padding:32,maxWidth:420,width:'100%',textAlign:'center',boxShadow:'0 40px 100px rgba(0,0,0,.2)'}}>
            <div style={{fontSize:'3rem',marginBottom:12}}>🔒</div>
            <h3 style={{fontWeight:800,fontSize:'1.1rem',color:'#1e293b',marginBottom:8}}>Límite del plan Free</h3>
            <p style={{fontSize:'.88rem',color:'#64748b',marginBottom:20,lineHeight:1.6}}>
              El plan <strong>Free</strong> solo permite <strong>1 negocio</strong>. Actualiza tu plan para crear más negocios.
            </p>
            <div style={{background:'#f8fafc',borderRadius:12,padding:16,marginBottom:20,textAlign:'left'}}>
              <p style={{fontSize:'.78rem',fontWeight:700,color:'#475569',marginBottom:8}}>Con planes de pago obtienes:</p>
              <div style={{display:'flex',flexDirection:'column',gap:6,fontSize:'.80rem',color:'#64748b'}}>
                <span>✅ Múltiples negocios</span>
                <span>✅ Badge verificado en el directorio</span>
                <span>✅ Posición destacada en búsquedas</span>
                <span>✅ Eventos ilimitados</span>
              </div>
            </div>
            <div style={{display:'flex',gap:10}}>
              <button onClick={() => setPlanAlert(false)} style={{flex:1,padding:'11px',borderRadius:10,border:'1px solid #e2e8f0',background:'white',fontWeight:600,fontSize:'.85rem',cursor:'pointer',fontFamily:'inherit',color:'#475569'}}>
                Cerrar
              </button>
              <a href="/subscription" onClick={() => setPlanAlert(false)} style={{flex:2,padding:'11px',borderRadius:10,border:'none',background:'#3b82f6',color:'white',fontWeight:700,fontSize:'.85rem',cursor:'pointer',fontFamily:'inherit',textDecoration:'none',display:'flex',alignItems:'center',justifyContent:'center'}}>
                ⬆️ Ver planes
              </a>
            </div>
          </div>
        </div>
      )}

      {/* MODAL ALERTA PLAN */}
      {planAlert && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,.6)',zIndex:300,display:'flex',alignItems:'center',justifyContent:'center',padding:20,backdropFilter:'blur(8px)'}}>
          <div style={{background:'white',borderRadius:24,padding:36,maxWidth:400,width:'100%',textAlign:'center',boxShadow:'0 40px 100px rgba(0,0,0,.25)',animation:'fadeIn .2s ease'}}>
            <div style={{width:64,height:64,background:'linear-gradient(135deg,#fef3c7,#fde68a)',borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.8rem',margin:'0 auto 20px'}}>🔒</div>
            <h3 style={{fontWeight:800,fontSize:'1.15rem',color:'#1e293b',marginBottom:8}}>Plan Free — 1 negocio</h3>
            <p style={{fontSize:'.88rem',color:'#64748b',lineHeight:1.7,marginBottom:24}}>
              Con el plan <strong>Free</strong> puedes tener <strong>1 negocio</strong>. Actualiza tu plan para añadir más y aparecer destacado en el directorio.
            </p>
            <div style={{background:'#f8fafc',borderRadius:14,padding:16,marginBottom:24,textAlign:'left'}}>
              {[['⚡ Starter','Más eventos, badge verificado'],['🚀 Pro','Primera posición en búsquedas'],['💎 Business','Negocios ilimitados + todo incluido']].map(([plan,desc]) => (
                <div key={plan} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 0',borderBottom:'1px solid #f1f5f9'}}>
                  <span style={{fontWeight:700,fontSize:'.82rem',color:'#1e293b',width:90}}>{plan}</span>
                  <span style={{fontSize:'.78rem',color:'#64748b'}}>{desc}</span>
                </div>
              ))}
            </div>
            <div style={{display:'flex',gap:10}}>
              <button onClick={() => setPlanAlert(false)} style={{flex:1,padding:'12px',borderRadius:12,border:'1.5px solid #e2e8f0',background:'white',fontWeight:600,fontSize:'.85rem',cursor:'pointer',fontFamily:'inherit',color:'#475569',transition:'all .2s'}}>
                Ahora no
              </button>
              <a href="/subscription" onClick={() => setPlanAlert(false)} style={{flex:2,padding:'12px',borderRadius:12,background:'linear-gradient(135deg,#2563eb,#7c3aed)',color:'white',fontWeight:700,fontSize:'.85rem',cursor:'pointer',fontFamily:'inherit',textDecoration:'none',display:'flex',alignItems:'center',justifyContent:'center',gap:6,boxShadow:'0 4px 14px rgba(37,99,235,.35)'}}>
                ⬆️ Ver planes
              </a>
            </div>
          </div>
        </div>
      )}

      <Modal isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setEditingBusiness(null); }} title={editingBusiness ? 'Editar negocio' : 'Nuevo negocio'} maxWidth="lg">
        <style>{`
          .nf { width:100%; padding:11px 14px; border:1.5px solid #e2e8f0; border-radius:10px; font-size:.88rem; font-family:inherit; outline:none; color:#1e293b; transition:border-color .2s; background:white; }
          .nf:focus { border-color:#3b82f6; box-shadow:0 0 0 3px rgba(59,130,246,.1); }
          .nf::placeholder { color:#c8d6e5; }
          .nfl { display:block; font-size:.75rem; font-weight:600; color:#64748b; margin-bottom:5px; }
          .nfg { margin-bottom:14px; }
          .nfr { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
          .nfs { font-size:.68rem; font-weight:700; text-transform:uppercase; letter-spacing:.1em; color:#94a3b8; margin:16px 0 10px; display:flex; align-items:center; gap:8px; }
          .nfs::before,.nfs::after { content:''; flex:1; border-top:1px solid #f1f5f9; }
          .nf-prefix { display:flex; border:1.5px solid #e2e8f0; border-radius:10px; overflow:hidden; transition:border-color .2s; }
          .nf-prefix:focus-within { border-color:#3b82f6; box-shadow:0 0 0 3px rgba(59,130,246,.1); }
          .nf-prefix span { background:#f8fafc; padding:11px 12px; color:#94a3b8; font-size:.82rem; border-right:1px solid #e2e8f0; flex-shrink:0; }
          .nf-prefix input { flex:1; border:none; padding:11px 12px; font-size:.88rem; font-family:inherit; outline:none; color:#1e293b; background:white; min-width:0; }
          .nf-prefix input::placeholder { color:#c8d6e5; }
          .nf-cats { display:grid; grid-template-columns:repeat(4,1fr); gap:6px; margin-bottom:14px; }
          .nf-cat { padding:9px 4px; border:1.5px solid #e2e8f0; border-radius:10px; background:white; cursor:pointer; font-size:.68rem; font-weight:600; color:#64748b; font-family:inherit; text-align:center; transition:all .15s; }
          .nf-cat:hover { border-color:#93c5fd; background:#eff6ff; color:#2563eb; }
          @media(max-width:480px) { .nfr { grid-template-columns:1fr; } .nf-cats { grid-template-columns:repeat(3,1fr); } }
        `}</style>
        <form onSubmit={handleSubmit(onSubmit)} style={{display:'flex',flexDirection:'column',gap:0}}>

          <div className="nfg">
            <label className="nfl">Nombre del negocio *</label>
            <input {...register('name', { required: 'El nombre es obligatorio' })} className="nf" placeholder="Ej: CrossFit Madrid Centro" />
            {errors.name && <p style={{fontSize:'.72rem',color:'#ef4444',marginTop:3}}>{errors.name.message}</p>}
          </div>

          <div className="nfg">
            <label className="nfl">Descripción</label>
            <textarea {...register('description')} className="nf" rows={2} placeholder="¿Qué ofreces? Tu especialidad, instalaciones..." style={{resize:'none'}} />
          </div>

          <div className="nfs">Categoría</div>
          <div className="nf-cats">
            {[['🏋️','Gimnasio'],['⚡','CrossFit'],['🧘','Yoga'],['🤸','Pilates'],['🏊','Natación'],['🥊','Boxeo'],['💃','Danza'],['🌿','Bienestar'],['🎾','Padel'],['🏃','Atletismo'],['🧗','Escalada'],['🏢','Otra']].map(([icon,cat]) => (
              <button key={cat} type="button" onClick={() => {
                const el = document.querySelector('select[name="category"]') as HTMLSelectElement;
                if(el){ el.value=cat; el.dispatchEvent(new Event('change',{bubbles:true})); }
              }}
              style={{padding:'8px 4px',border:'1.5px solid #e2e8f0',borderRadius:10,background:'white',cursor:'pointer',fontSize:'.7rem',fontWeight:600,color:'#64748b',fontFamily:'inherit',textAlign:'center',transition:'all .15s'}}
              onMouseOver={e=>{(e.currentTarget as any).style.borderColor='#93c5fd';(e.currentTarget as any).style.background='#eff6ff';(e.currentTarget as any).style.color='#2563eb'}}
              onMouseOut={e=>{(e.currentTarget as any).style.borderColor='#e2e8f0';(e.currentTarget as any).style.background='white';(e.currentTarget as any).style.color='#64748b'}}>
                <div style={{fontSize:'1.1rem',marginBottom:2}}>{icon}</div>{cat}
              </button>
            ))}
          </div>
          <div className="nfg">
            <select {...register('category')} name="category" className="nf">
              <option value="">— O elige aquí —</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          <div className="nfs">Ubicación y contacto</div>
          <div className="nfr nfg">
            <div>
              <label className="nfl">Ciudad</label>
              <input {...register('city')} className="nf" placeholder="Madrid" />
            </div>
            <div>
              <label className="nfl">Dirección</label>
              <input {...register('address')} className="nf" placeholder="Calle Mayor 1" />
            </div>
          </div>
          <div className="nfr nfg">
            <div>
              <label className="nfl">Teléfono</label>
              <input {...register('phone')} type="tel" className="nf" placeholder="+34 600 000 000" />
            </div>
            <div>
              <label className="nfl">Email</label>
              <input {...register('email')} type="email" className="nf" placeholder="info@negocio.com" />
            </div>
          </div>
          <div className="nfg">
            <label className="nfl">Sitio web</label>
            <input {...register('website')} className="nf" placeholder="https://tunegocio.com" />
          </div>

          <div className="nfs">Redes sociales</div>
          <div className="nfr nfg">
            <div>
              <label className="nfl">Instagram</label>
              <div className="nf-prefix">
                <span>📸 @</span>
                <input {...register('instagram')} placeholder="tunegocio" />
              </div>
            </div>
            <div>
              <label className="nfl">WhatsApp</label>
              <div className="nf-prefix">
                <span>💬</span>
                <input {...register('whatsapp')} placeholder="34600000000" />
              </div>
            </div>
          </div>

          <div style={{display:'flex',gap:10,paddingTop:8,borderTop:'1px solid #f1f5f9',marginTop:4}}>
            <button type="button" onClick={() => { setIsModalOpen(false); setEditingBusiness(null); }}
              style={{flex:1,padding:'12px',borderRadius:11,border:'1.5px solid #e2e8f0',background:'white',fontWeight:600,fontSize:'.88rem',cursor:'pointer',fontFamily:'inherit',color:'#475569'}}>
              Cancelar
            </button>
            <button type="submit" disabled={isSubmitting}
              style={{flex:2,padding:'12px',borderRadius:11,border:'none',background:isSubmitting?'#93c5fd':'#2563eb',color:'white',fontWeight:700,fontSize:'.9rem',cursor:isSubmitting?'not-allowed':'pointer',fontFamily:'inherit',boxShadow:'0 4px 14px rgba(37,99,235,.3)',transition:'all .2s'}}>
              {isSubmitting ? '⏳ Guardando...' : editingBusiness ? '✅ Actualizar negocio' : '🚀 Crear negocio'}
            </button>
          </div>
        </form>
      </Modal>    </div>
  );
}
